// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/LauncherOrgToolbar.test.tsx
import {
  BookmarkSimple,
  Check,
  MagnifyingGlass,
  SortAscending,
} from "@phosphor-icons/react";
import {
  Button,
  Checkbox,
  Dialog,
  DropdownMenu,
  Flex,
  Text,
  TextField,
} from "@radix-ui/themes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  FiltersOverflowMenu,
  LAUNCHER_SORT_OPTIONS,
  PeopleFilter,
  TagFilter,
} from "./launcherOrgFilterLists";

import {
  TOOLBAR_CONTROL_SIZE,
  TOOLBAR_ICON_SIZE,
} from "../../../components/toolbarControls";
import {
  type LauncherSavedView,
  type LauncherSurface,
  type OrgLabel,
} from "../../../services/askPunttOrgFilters";
import {
  type LauncherSearch,
  type LauncherSort,
} from "../../../utils/askPunttLauncherSearch";
import {
  useCreateLauncherView,
  useLauncherViews,
  useOrgLabels,
} from "../../../utils/queries/askPunttOrgFilters";
import { useCompactLauncherToolbar } from "../hooks/useCompactLauncherToolbar";

export type LauncherOrgToolbarProps = {
  /** Prefix for all testids: "campaigns" | "chats". */
  testIdPrefix: string;
  /** Launcher surface for saved views scoping. */
  surface: LauncherSurface;
  /** Current search filters from URL. */
  search: LauncherSearch;
  /** Called when any filter changes. */
  onSearchChange: (patch: Partial<LauncherSearch>) => void;
  /** Local search input value (parent manages debouncing). */
  searchValue: string;
  onSearchInputChange: (value: string) => void;
  /** Placeholder for the search field. */
  searchPlaceholder: string;
  /** People available for the person filter (from list membership / uploaders). */
  peopleOptions?: { _id: string; name: string }[];
  /**
   * Tag slugs known from the current list (unioned with org-labels catalog so
   * pickers stay useful even when the catalog API lags).
   */
  knownTagSlugs?: string[];
  /** Additional toolbar controls (view toggle, new button). */
  children?: React.ReactNode;
};

/** Merge catalog labels with freeform slugs (case-insensitive slug key). */
export function mergeOrgLabelOptions(
  catalog: OrgLabel[],
  extraSlugs: string[],
): OrgLabel[] {
  const bySlug = new Map<string, OrgLabel>();
  for (const option of catalog) {
    const key = option.slug.trim().toLowerCase();
    if (!key) continue;
    bySlug.set(key, option);
  }
  for (const raw of extraSlugs) {
    const slug = raw.trim();
    if (!slug) continue;
    const key = slug.toLowerCase();
    if (bySlug.has(key)) continue;
    bySlug.set(key, { _id: slug, slug, name: slug });
  }
  return Array.from(bySlug.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

function hasOrgFilters(search: LauncherSearch): boolean {
  return (
    (search.people?.length ?? 0) > 0 ||
    (search.tags?.length ?? 0) > 0 ||
    Boolean(search.q?.trim())
  );
}

/**
 * Shared org toolbar for Ask Puntt list launchers (campaigns, chats).
 * Renders search, People/Tags filters, Sort, and Save-view controls with
 * prefixed testids. Mine and Regions were removed from product UX.
 * Narrow widths collapse People/Tags/Sort/Save view into a Filters menu.
 */
export function LauncherOrgToolbar({
  testIdPrefix,
  surface,
  search,
  onSearchChange,
  searchValue,
  onSearchInputChange,
  searchPlaceholder,
  peopleOptions = [],
  knownTagSlugs = [],
  children,
}: LauncherOrgToolbarProps) {
  const { t } = useTranslation("ui");
  const { data: orgLabels } = useOrgLabels();
  const { data: viewsData } = useLauncherViews(surface);
  const createView = useCreateLauncherView();
  const [viewsMenuOpen, setViewsMenuOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const [newViewDefault, setNewViewDefault] = useState(false);
  const defaultAppliedRef = useRef(false);
  const { ref: measureRef, compact } = useCompactLauncherToolbar();

  const tags = useMemo(
    () =>
      mergeOrgLabelOptions(orgLabels?.tags ?? [], [
        ...knownTagSlugs,
        ...(search.tags ?? []),
      ]),
    [orgLabels?.tags, knownTagSlugs, search.tags],
  );
  const savedViews = (viewsData?.views ?? []).filter(
    (v) => v.surface == null || v.surface === surface,
  );
  const activeSort: LauncherSort = search.sort ?? "updatedAt";
  const activeSortOption =
    LAUNCHER_SORT_OPTIONS.find((o) => o.value === activeSort) ??
    LAUNCHER_SORT_OPTIONS[0];

  // Apply the per-surface default view once when the URL has no org filters.
  useEffect(() => {
    if (defaultAppliedRef.current) return;
    if (hasOrgFilters(search)) {
      defaultAppliedRef.current = true;
      return;
    }
    if (viewsData == null) return;
    const defaultView = savedViews.find((v) => v.isDefault === true);
    defaultAppliedRef.current = true;
    if (defaultView == null) return;
    onSearchChange({
      people: defaultView.filters.people,
      tags: defaultView.filters.tags,
      sort: defaultView.filters.sort as LauncherSort | undefined,
    });
    // Intentionally omit `search` / `savedViews` / `onSearchChange` from deps:
    // this must run once when views load against a filter-empty URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot default apply
  }, [viewsData]);

  const toggleArrayItem = useCallback(
    (key: "people" | "tags", item: string, checked: boolean) => {
      const current = search[key] ?? [];
      const next = checked
        ? [...current, item]
        : current.filter((v) => v.toLowerCase() !== item.toLowerCase());
      onSearchChange({ [key]: next.length > 0 ? next : undefined });
    },
    [search, onSearchChange],
  );

  const applySavedView = useCallback(
    (view: LauncherSavedView) => {
      onSearchChange({
        people: view.filters.people,
        tags: view.filters.tags,
        sort: (view.filters.sort as LauncherSort | undefined) ?? "updatedAt",
      });
    },
    [onSearchChange],
  );

  const handleCreateView = () => {
    const name = newViewName.trim();
    if (!name) return;
    createView.mutate(
      {
        name,
        surface,
        filters: {
          people: search.people,
          tags: search.tags,
          sort: search.sort ?? "updatedAt",
        },
        isDefault: newViewDefault,
      },
      {
        onSuccess: () => {
          setSaveDialogOpen(false);
          setNewViewName("");
          setNewViewDefault(false);
        },
      },
    );
  };

  return (
    // why: flex-1 so ResizeObserver sees available header width, not shrunk content after compact
    <div ref={measureRef} className="flex min-w-0 flex-1 justify-end">
      <Flex align="center" gap="2" wrap="nowrap" className="min-w-0">
        <TextField.Root
          data-testid={`${testIdPrefix}-search`}
          placeholder={searchPlaceholder}
          size={TOOLBAR_CONTROL_SIZE}
          value={searchValue}
          onChange={(e) => onSearchInputChange(e.target.value)}
          aria-label={searchPlaceholder}
          className={
            compact ? "w-48 min-w-32 max-w-64" : "min-w-[200px] shrink-0"
          }
        >
          <TextField.Slot>
            <MagnifyingGlass size={TOOLBAR_ICON_SIZE} aria-hidden="true" />
          </TextField.Slot>
        </TextField.Root>

        {compact ? (
          <FiltersOverflowMenu
            testIdPrefix={testIdPrefix}
            search={search}
            peopleOptions={peopleOptions}
            tags={tags}
            savedViews={savedViews}
            activeSort={activeSort}
            onTogglePeople={(id, checked) =>
              toggleArrayItem("people", id, checked)
            }
            onClearPeople={() => onSearchChange({ people: undefined })}
            onToggleTags={(slug, checked) =>
              toggleArrayItem("tags", slug, checked)
            }
            onClearTags={() => onSearchChange({ tags: undefined })}
            onSort={(sort) => onSearchChange({ sort })}
            onApplyView={applySavedView}
            onRequestSaveView={() => setSaveDialogOpen(true)}
          />
        ) : (
          <>
            <PeopleFilter
              testIdPrefix={testIdPrefix}
              selected={search.people ?? []}
              onToggle={(id, checked) => toggleArrayItem("people", id, checked)}
              onClear={() => onSearchChange({ people: undefined })}
              peopleOptions={peopleOptions}
            />

            <TagFilter
              testIdPrefix={testIdPrefix}
              label={t("tags", { defaultValue: "Tags" })}
              options={tags}
              selected={search.tags ?? []}
              onToggle={(slug, checked) =>
                toggleArrayItem("tags", slug, checked)
              }
              onClear={() => onSearchChange({ tags: undefined })}
            />

            <DropdownMenu.Root>
              <DropdownMenu.Trigger>
                <Button
                  type="button"
                  variant="surface"
                  color="gray"
                  size={TOOLBAR_CONTROL_SIZE}
                  data-testid={`${testIdPrefix}-sort`}
                  aria-label={`Sort: ${activeSortOption.fallback}`}
                  className="shrink-0 cursor-pointer"
                >
                  <SortAscending size={TOOLBAR_ICON_SIZE} aria-hidden="true" />
                  <Text
                    size="2"
                    weight="regular"
                    className="text-puntt-neutral-gray-11"
                  >
                    {t(activeSortOption.labelKey, {
                      defaultValue: activeSortOption.fallback,
                    })}
                  </Text>
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content>
                {LAUNCHER_SORT_OPTIONS.map((option) => {
                  const isActive = option.value === activeSort;
                  return (
                    <DropdownMenu.Item
                      key={option.value}
                      data-testid={`${testIdPrefix}-sort-${option.value}`}
                      onSelect={() => onSearchChange({ sort: option.value })}
                    >
                      <Flex align="center" gap="2" className="w-full">
                        <Check
                          size={14}
                          aria-hidden="true"
                          className={isActive ? "opacity-100" : "opacity-0"}
                        />
                        {t(option.labelKey, { defaultValue: option.fallback })}
                      </Flex>
                    </DropdownMenu.Item>
                  );
                })}
              </DropdownMenu.Content>
            </DropdownMenu.Root>

            <DropdownMenu.Root
              open={viewsMenuOpen}
              onOpenChange={setViewsMenuOpen}
            >
              <DropdownMenu.Trigger>
                <Button
                  type="button"
                  variant="surface"
                  color="gray"
                  size={TOOLBAR_CONTROL_SIZE}
                  data-testid={`${testIdPrefix}-save-view`}
                  aria-label="Saved views"
                  className="shrink-0 cursor-pointer"
                >
                  <BookmarkSimple size={TOOLBAR_ICON_SIZE} aria-hidden="true" />
                  <Text
                    size="2"
                    weight="regular"
                    className="text-puntt-neutral-gray-11"
                  >
                    {t("saveView", { defaultValue: "Save view" })}
                  </Text>
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content>
                <DropdownMenu.Item
                  data-testid={`${testIdPrefix}-save-view-create`}
                  onSelect={() => {
                    setViewsMenuOpen(false);
                    setSaveDialogOpen(true);
                  }}
                >
                  {t("saveCurrentView", {
                    defaultValue: "Save current filters…",
                  })}
                </DropdownMenu.Item>
                {savedViews.length === 0 ? (
                  <DropdownMenu.Item disabled>
                    {t("noSavedViews", { defaultValue: "No saved views" })}
                  </DropdownMenu.Item>
                ) : (
                  savedViews.map((view) => (
                    <DropdownMenu.Item
                      key={view._id}
                      data-testid={`${testIdPrefix}-saved-view-${view._id}`}
                      onSelect={() => {
                        applySavedView(view);
                        setViewsMenuOpen(false);
                      }}
                    >
                      <BookmarkSimple size={14} aria-hidden="true" />
                      {view.name}
                      {view.isDefault ? " *" : ""}
                    </DropdownMenu.Item>
                  ))
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </>
        )}

        <Dialog.Root open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
          <Dialog.Content maxWidth="400px">
            <Dialog.Title>
              {t("saveView", { defaultValue: "Save view" })}
            </Dialog.Title>
            <Dialog.Description size="2" mb="3">
              {t("saveViewDescription", {
                defaultValue: "Name this filter set for quick reuse.",
              })}
            </Dialog.Description>
            <Flex direction="column" gap="3">
              <TextField.Root
                data-testid={`${testIdPrefix}-save-view-name`}
                placeholder={t("viewName", { defaultValue: "View name" })}
                aria-label={t("viewName", { defaultValue: "View name" })}
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
              />
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={newViewDefault}
                  onCheckedChange={(v) => setNewViewDefault(v === true)}
                  data-testid={`${testIdPrefix}-save-view-default`}
                />
                {t("setAsDefaultView", {
                  defaultValue: "Set as default for this launcher",
                })}
              </label>
              <Flex gap="2" justify="end">
                <Dialog.Close>
                  <Button variant="soft" color="gray">
                    {t("cancel", { defaultValue: "Cancel" })}
                  </Button>
                </Dialog.Close>
                <Button
                  data-testid={`${testIdPrefix}-save-view-confirm`}
                  disabled={!newViewName.trim() || createView.isPending}
                  onClick={handleCreateView}
                >
                  {t("save", { defaultValue: "Save" })}
                </Button>
              </Flex>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>

        {children}
      </Flex>
    </div>
  );
}
